<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { actingContextMessageKey, getErrorStatus, handleAxiosError } from '@/composables/useAxiosError'
import { useSwitchText } from '@/composables/useSwitchText'
import { type SwitchTermsBody, useSwitchTerms } from '@/composables/useSwitchTerms'
import { intervalKey, termPeriod, uuid } from '@/composables'
import { useNotifyStore } from '@/stores'
import { BillingPeriodState, type BillingPeriodOption } from '@/types'
import type { PaymentInterval } from '@/types/purchase/PaymentInterval'
import { STATUS_CODES } from '@/constants'
import { berlinDay, isTermsStale, needsTerms, termMoney, termSwitchError } from '@/utils/termSwitch'
import TermsStaleNotice from '@/components/misc/TermsStaleNotice.vue'
import SwitchTermsDeclaration from './SwitchTermsDeclaration.vue'

const props = defineProps<{
  modelValue: boolean
  subscriptionId: number
  // A vServer bills monthly by the hour, so its monthly option names a cap.
  hourlyMonthly?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'changed'): void
}>()

const { t } = useI18n()
const notifyStore = useNotifyStore()
const switchText = useSwitchText()

const state = ref<BillingPeriodState | null>(null)
const loading = ref(false)
// The backend refused the change before anything was chosen.
const refused = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const selected = ref<PaymentInterval | null>(null)

const {
  accepted: termsAccepted,
  stale: termsStale,
  ready: termsReady,
  checkVersion,
  markStale,
  reloadForTerms,
  body: termsBody
} = useSwitchTerms(() => state.value?.terms ?? null)

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

const url = () => `/subscription/${props.subscriptionId}/billing-period`
const periodName = (freq: PaymentInterval) => t(`common.billingPeriod.periods.${intervalKey(freq)}`)

const scheduled = computed(() => {
  const s = state.value
  return s?.pending_billing_freq && s.pending_from ? { period: periodName(s.pending_billing_freq), date: berlinDay(s.pending_from) } : null
})

const money = (cents: number) => termMoney(cents, switchText.value)

const optionPrice = (option: BillingPeriodOption) =>
  props.hourlyMonthly && option.billing_freq === 'monthly'
    ? t('common.billingPeriod.dialog.hourlyPrice', { price: money(option.price_cents) })
    : t('common.billingPeriod.dialog.price', { price: money(option.price_cents), period: termPeriod(option.billing_freq, t) })

async function load() {
  state.value = null
  refused.value = false
  termsAccepted.value = false
  loading.value = true
  try {
    const res = await useAxios<null, BillingPeriodState>('GET', url(), true)
    const parsed = BillingPeriodState.safeParse(res?.data)
    if (parsed.success) state.value = parsed.data
  } catch (err) {
    const status = getErrorStatus(err)
    refused.value = true
    errorMessage.value = termSwitchError(err, t)
    const expected = status === STATUS_CODES.CONFLICT || status === STATUS_CODES.BAD_REQUEST || !!actingContextMessageKey(err)
    handleAxiosError(err, { logPrefix: 'BillingPeriodDialog:load', silentStatuses: expected && status ? [status] : [] })
  } finally {
    loading.value = false
    // A reload for the terms keeps the period chosen while it is still offered.
    if (!state.value?.options.some((o) => o.billing_freq === selected.value)) selected.value = null
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    errorMessage.value = ''
    void checkVersion()
    void load()
  },
  { immediate: true }
)

function close() {
  if (saving.value) return
  isOpen.value = false
}

async function confirm() {
  if (!selected.value) return
  saving.value = true
  errorMessage.value = ''
  try {
    const body: SwitchTermsBody & { billing_freq: PaymentInterval } = { billing_freq: selected.value, ...termsBody() }
    await useAxios<typeof body, unknown>('POST', url(), true, body)
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('common.billingPeriod.label'),
      message: t('common.billingPeriod.dialog.success')
    })
    isOpen.value = false
    emit('changed')
  } catch (err) {
    errorMessage.value = isTermsStale(err) ? '' : termSwitchError(err, t)
    handleAxiosError(err, {
      logPrefix: 'BillingPeriodDialog',
      silentStatuses: [STATUS_CODES.CONFLICT, STATUS_CODES.BAD_REQUEST, STATUS_CODES.FORBIDDEN]
    })
    if (isTermsStale(err)) markStale()
    else if (needsTerms(err)) await load()
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <v-dialog v-model="isOpen" max-width="520" :persistent="saving" class="billing-period-dialog">
    <div class="period-modal">
      <div class="modal-header">
        <div class="header-icon">
          <v-icon icon="mdi-calendar-sync-outline" size="24" />
        </div>
        <h2 class="modal-title">{{ $t('common.billingPeriod.dialog.title') }}</h2>
        <button type="button" class="close-btn" :disabled="saving" @click="close">
          <v-icon icon="mdi-close" size="20" />
        </button>
      </div>

      <div class="modal-content">
        <div v-if="loading" class="info-note">
          <v-progress-circular indeterminate size="16" width="2" />
        </div>

        <template v-else-if="state">
          <p class="current" data-test="period-current">
            {{ $t('common.billingPeriod.dialog.current', { period: periodName(state.billing_freq) }) }}
          </p>

          <p v-if="scheduled" class="info-note" data-test="period-scheduled">
            {{ $t('common.billingPeriod.dialog.scheduled', scheduled) }}
          </p>
          <template v-else>
            <p v-if="state.options.length === 0" class="info-note">{{ $t('common.billingPeriod.dialog.none') }}</p>
            <div v-else class="options" role="radiogroup">
              <label
                v-for="option in state.options"
                :key="option.billing_freq"
                class="option"
                :class="{ active: selected === option.billing_freq }"
                :data-test="`period-option-${option.billing_freq}`"
              >
                <input v-model="selected" type="radio" name="billing-period" :value="option.billing_freq" />
                <span class="option-body">
                  <span class="option-name">{{ periodName(option.billing_freq) }}</span>
                  <span class="option-price">{{ optionPrice(option) }}</span>
                  <span v-if="option.billing_freq !== 'monthly'" class="option-equivalent">
                    {{ $t('common.termPrice.monthlyEquivalent', { price: money(option.monthly_cents) }) }}
                  </span>
                  <span class="option-date">{{ $t('common.billingPeriod.dialog.from', { date: berlinDay(state.effective_at) }) }}</span>
                </span>
              </label>
            </div>

            <p class="info-note" data-test="period-effective">
              {{ $t('common.billingPeriod.dialog.effective', { date: berlinDay(state.effective_at) }) }}
            </p>

            <SwitchTermsDeclaration v-model="termsAccepted" :terms="state.terms" />
          </template>
        </template>

        <TermsStaleNotice v-if="termsStale" @reload="reloadForTerms" />

        <div v-if="errorMessage" class="error-note" data-test="period-error">
          <v-icon icon="mdi-alert-circle-outline" size="16" class="error-icon" />
          <span>{{ errorMessage }}</span>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="close">
          {{ $t('common.billingPeriod.dialog.cancel') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          data-test="period-confirm"
          :disabled="saving || loading || refused || !state || !!scheduled || !selected || !termsReady()"
          @click="confirm"
        >
          <v-progress-circular v-if="saving" indeterminate size="18" width="2" />
          <v-icon v-else icon="mdi-check" size="18" />
          <span>{{ $t('common.billingPeriod.dialog.confirm') }}</span>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.period-modal {
  background: rgba(15, 23, 48, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  flex-shrink: 0;
  background: rgba(0, 176, 255, 0.12);
  border: 1px solid rgba(0, 176, 255, 0.25);
  color: #00b0ff;
}

.modal-title {
  flex: 1;
  min-width: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.modal-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.current {
  margin: 0;
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.8);
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(120, 163, 255, 0.04);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  cursor: pointer;

  &.active {
    background: rgba(0, 176, 255, 0.08);
    border-color: rgba(0, 176, 255, 0.35);
  }

  input {
    margin-top: 4px;
    accent-color: #00b0ff;
  }
}

.option-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
}

.option-price {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.75);
}

.option-equivalent,
.option-date {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.info-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 14px 16px;
  background: rgba(0, 176, 255, 0.04);
  border: 1px solid rgba(0, 176, 255, 0.12);
  border-radius: 10px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
  line-height: 1.5;
}

.error-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: rgba(244, 67, 54, 0.06);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 10px;
  font-size: 0.85rem;
  color: #ef5350;
  line-height: 1.5;
}

.error-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &-secondary {
    background: rgba(120, 163, 255, 0.08);
    border-color: rgba(120, 163, 255, 0.15);
    color: rgba(200, 218, 255, 0.8);
  }

  &-primary {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00b0ff;
  }
}
</style>
